import React, { useState, useEffect, useMemo } from 'react';
import api, { scenariosAPI, lifecycleAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Calculator, AlertTriangle, TrendingUp, Target, CheckCircle2, Sparkles,
  Save, Trash2, Plus, Layers,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export const RetirementToolsPage = () => {
  const [tsp, setTsp] = useState(null);
  const [loading, setLoading] = useState(true);
  // FERS Pension inputs
  const [high3, setHigh3] = useState(85000);
  const [yearsService, setYearsService] = useState(20);
  const [retireAge, setRetireAge] = useState(62);
  // FERS Match calculator
  const [salary, setSalary] = useState(85000);
  const [empPct, setEmpPct] = useState(5);
  const [matchResult, setMatchResult] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tsp/summary');
      setTsp(res.data);
      setSalary(res.data?.account?.annual_salary || 85000);
      setYearsService(Math.round(res.data?.vesting?.years_of_service || 20));
      setHigh3(res.data?.account?.annual_salary || 85000);
    } catch {} finally { setLoading(false); }
  };

  const calcPension = () => {
    // 5 U.S.C. § 8415: 1% x High-3 x years; 1.1% if age 62+ AND 20+ years
    const multiplier = (retireAge >= 62 && yearsService >= 20) ? 0.011 : 0.01;
    return high3 * multiplier * yearsService;
  };

  const calcMatch = async () => {
    try {
      const res = await api.get(`/tsp/fers-match-calculator?salary=${salary}&employee_contribution_pct=${empPct}`);
      setMatchResult(res.data);
    } catch {}
  };

  useEffect(() => { if (salary > 0 && empPct >= 0) calcMatch(); }, [salary, empPct]);

  const pension = calcPension();
  const rmd = tsp?.rmd || {};
  const bal = tsp?.balances || {};

  // Withdrawal calc
  const withdrawalAmount = bal.total || 0;
  const traditionalPortion = bal.traditional || 0;
  const rothPortion = bal.roth || 0;
  const tradPct = withdrawalAmount > 0 ? (traditionalPortion / withdrawalAmount * 100) : 0;
  const rothPct = withdrawalAmount > 0 ? (rothPortion / withdrawalAmount * 100) : 0;
  const federalWithholding = traditionalPortion * 0.20;

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";

  return (
    <div className="space-y-4" data-testid="retirement-tools-page">
      <h1 className="text-lg font-semibold text-[#1B2A4A]" style={{ fontFamily: 'IBM Plex Sans' }}>Retirement Planning Tools</h1>

      {/* Retirement Goal Calculator (Fidelity-style — live-updating primary) */}
      <RetirementGoalCalculator
        currentBalanceDefault={bal.total || 0}
        salaryDefault={tsp?.account?.annual_salary || 85000}
        ageDefault={rmd.current_age || 38}
      />

      {/* FERS Pension Estimator */}
      <div className="panel p-5" data-testid="pension-estimator">
        <span className="overline mb-4 block">FERS Pension Estimator — 5 U.S.C. § 8415</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">High-3 Average Salary</label>
            <input type="number" value={high3} onChange={e => setHigh3(parseInt(e.target.value) || 0)} className={inputClass} data-testid="high3-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Years of Service</label>
            <input type="number" value={yearsService} onChange={e => setYearsService(parseInt(e.target.value) || 0)} className={inputClass} data-testid="years-service-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Retirement Age</label>
            <input type="number" value={retireAge} onChange={e => setRetireAge(parseInt(e.target.value) || 0)} className={inputClass} data-testid="retire-age-input" />
          </div>
        </div>
        <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-[#6B7280]">Estimated Annual Pension</span>
              <div className="font-mono text-2xl font-semibold text-[#1B2A4A]" data-testid="pension-result">{fmt(pension)}</div>
              <span className="text-xs text-[#9CA3AF]">{fmt(pension / 12)}/month</span>
            </div>
            <div className="text-right text-xs text-[#6B7280]">
              <p>Multiplier: {retireAge >= 62 && yearsService >= 20 ? '1.1%' : '1.0%'}</p>
              <p>Formula: {retireAge >= 62 && yearsService >= 20 ? '1.1%' : '1.0%'} x {fmt(high3)} x {yearsService} yrs</p>
              {retireAge >= 62 && yearsService >= 20 && <p className="text-emerald-600 font-medium mt-1">1.1% bonus multiplier applied (age 62+ & 20+ yrs)</p>}
            </div>
          </div>
        </div>
      </div>

      {/* FERS Match What-If Calculator */}
      <div className="panel p-5" data-testid="match-calculator">
        <span className="overline mb-4 block">FERS Match Optimizer — What-If Calculator</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Annual Salary</label>
            <input type="number" value={salary} onChange={e => setSalary(parseInt(e.target.value) || 0)} className={inputClass} data-testid="match-salary-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#1B2A4A] mb-1">Your Contribution Rate (%)</label>
            <input type="range" min={0} max={15} step={0.5} value={empPct} onChange={e => setEmpPct(parseFloat(e.target.value))} className="w-full mt-2" data-testid="match-pct-slider" />
            <div className="flex justify-between text-xs text-[#6B7280] mt-1"><span>0%</span><span className="font-mono font-medium text-[#1B2A4A]">{empPct}%</span><span>15%</span></div>
          </div>
        </div>
        {matchResult && (
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4 space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><span className="text-[#9CA3AF]">Your Annual</span><div className="font-mono font-medium text-[#1B2A4A]">{fmtFull(matchResult.employee_annual)}</div></div>
              <div><span className="text-[#9CA3AF]">Agency Auto 1%</span><div className="font-mono font-medium text-[#1B2A4A]">{fmtFull(matchResult.agency_auto_1pct)}</div></div>
              <div><span className="text-[#9CA3AF]">Agency Match</span><div className="font-mono font-medium text-[#1B2A4A]">{fmtFull(matchResult.total_match)}</div></div>
              <div><span className="text-[#9CA3AF]">Total Retirement</span><div className="font-mono font-medium text-emerald-700">{fmtFull(matchResult.total_annual_retirement)}</div></div>
            </div>
            {!matchResult.capturing_full_match && empPct < 5 && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-xs text-amber-700">Increase to 5% to capture full FERS match. You're leaving <strong>{fmtFull((0.05 * salary) - (empPct/100 * salary))} + match</strong> on the table annually.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* RMD Status */}
      <div className="panel p-5" data-testid="rmd-section">
        <span className="overline mb-4 block">Required Minimum Distributions — SECURE 2.0</span>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-xs text-[#9CA3AF]">Current Age</span>
            <div className="font-mono text-xl font-semibold text-[#1B2A4A]">{rmd.current_age || '--'}</div>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-xs text-[#9CA3AF]">RMD Age</span>
            <div className="font-mono text-xl font-semibold text-[#1B2A4A]">{rmd.rmd_age || '--'}</div>
            <span className="text-[10px] text-[#9CA3AF]">Born {rmd.birth_year < 1960 ? '<1960: age 73' : '≥1960: age 75'}</span>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-xs text-[#9CA3AF]">Years Until RMD</span>
            <div className="font-mono text-xl font-semibold text-[#1B2A4A]">{rmd.years_until_rmd || '--'}</div>
          </div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
            <span className="text-xs text-[#9CA3AF]">Roth RMD</span>
            <div className="text-sm font-medium text-emerald-600 mt-1">Exempt</div>
            <span className="text-[10px] text-[#9CA3AF]">Effective 2024</span>
          </div>
        </div>
        <div className="mt-3 text-xs text-[#6B7280] space-y-1">
          <p>Still-working exception: delay RMD until separation from service.</p>
          <p>Missed RMD penalty: 25% excise tax, reducible to 10% within 2 years.</p>
        </div>
      </div>

      {/* Withdrawal Preview */}
      <div className="panel p-5" data-testid="withdrawal-preview">
        <span className="overline mb-4 block">Withdrawal Estimator — 26 U.S.C. § 72</span>
        <div className="bg-amber-50 border border-amber-200 rounded px-4 py-3 mb-4">
          <p className="text-xs text-amber-700 font-medium">20% mandatory federal withholding on eligible rollover distributions. Distributions are pro-rata from Traditional and Roth — you cannot choose one source.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-4 space-y-3">
            <span className="text-xs font-medium text-[#1B2A4A]">If Full Balance Withdrawn Today</span>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#6B7280]">Total Balance</span><span className="font-mono font-medium">{fmtFull(withdrawalAmount)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#6B7280]">Traditional ({tradPct.toFixed(1)}%)</span><span className="font-mono">{fmtFull(traditionalPortion)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-[#6B7280]">Roth ({rothPct.toFixed(1)}%)</span><span className="font-mono">{fmtFull(rothPortion)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#E2E5E9]"><span className="text-red-600 font-medium">Federal Withholding (20% of Traditional)</span><span className="font-mono text-red-600">-{fmtFull(federalWithholding)}</span></div>
              <div className="flex justify-between py-1"><span className="font-medium text-[#1B2A4A]">Net Distribution</span><span className="font-mono font-semibold text-[#1B2A4A]">{fmtFull(withdrawalAmount - federalWithholding)}</span></div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <span className="text-xs font-medium text-blue-800">Direct Rollover Option</span>
            <p className="text-xs text-blue-700 mt-2">A direct rollover to an eligible retirement plan avoids the 20% mandatory federal withholding. You must be offered this option before any distribution.</p>
            <p className="text-xs text-blue-700 mt-2 font-medium">Pro-rata rule: {tradPct.toFixed(1)}% of any withdrawal comes from Traditional (taxable) and {rothPct.toFixed(1)}% from Roth (tax-free).</p>
          </div>
        </div>
      </div>

      {/* Mutual Fund Window */}
      <div className="panel p-5" data-testid="mutual-fund-window">
        <span className="overline mb-3 block">Mutual Fund Window — UniFed Core Modernization Act 2017</span>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs">
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3"><span className="text-[#9CA3AF]">Min Retirement Balance</span><div className="font-mono font-medium text-[#1B2A4A]">$10,000</div></div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3"><span className="text-[#9CA3AF]">Max in Window</span><div className="font-mono font-medium text-[#1B2A4A]">25% of balance</div></div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3"><span className="text-[#9CA3AF]">Annual Fee</span><div className="font-mono font-medium text-[#1B2A4A]">$55/year</div></div>
          <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3"><span className="text-[#9CA3AF]">Per Trade Fee</span><div className="font-mono font-medium text-[#1B2A4A]">$28.75</div></div>
        </div>
        <p className="mt-3 text-xs text-[#6B7280]">75% of your balance must remain in UniFed Core Funds&trade; (A, B, C, D, E, and L Series). Your current balance of {fmt(bal.total)} {(bal.total || 0) >= 10000 ? 'qualifies' : 'does not yet qualify'} for the Mutual Fund Window.</p>
      </div>
    </div>
  );
};

// ─── Retirement Goal Calculator — live, Fidelity-style primary calculator ────
const fmt0 = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

const RetirementGoalCalculator = ({
  currentBalanceDefault,
  salaryDefault,
  ageDefault,
}) => {
  const [currentAge, setCurrentAge] = useState(ageDefault || 38);
  const [retireAge, setRetireAge] = useState(65);
  const [currentBalance, setCurrentBalance] = useState(currentBalanceDefault || 75000);
  const [salary, setSalary] = useState(salaryDefault || 85000);
  const [contribPct, setContribPct] = useState(5);
  const [expectedReturn, setExpectedReturn] = useState(7);
  const [inflation, setInflation] = useState(2.5);
  const [recalcPulse, setRecalcPulse] = useState(false);

  // Scenarios
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [savingScenario, setSavingScenario] = useState(false);
  const [compareIds, setCompareIds] = useState([]);  // scenario IDs to overlay on chart

  // Lifecycle suggestion
  const [lifecycleSuggestion, setLifecycleSuggestion] = useState(null);

  // Reconcile when defaults arrive
  useEffect(() => {
    if (currentBalanceDefault) setCurrentBalance(Math.round(currentBalanceDefault));
  }, [currentBalanceDefault]);
  useEffect(() => {
    if (salaryDefault) setSalary(Math.round(salaryDefault));
  }, [salaryDefault]);
  useEffect(() => {
    if (ageDefault) setCurrentAge(ageDefault);
  }, [ageDefault]);

  // ─── Projection math (live) ──────────────────────────────
  const projection = useMemo(() => {
    const years = Math.max(0, retireAge - currentAge);
    const r = expectedReturn / 100;
    const infl = inflation / 100;
    const annualContrib = salary * (contribPct / 100);
    // FERS match on first 5%: auto 1% + 3% on first 3% + 0.5% on next 2% = 5% max
    const effMatch = Math.min(contribPct, 5);
    const agencyMatchPct =
      1 + Math.min(3, effMatch) + 0.5 * Math.max(0, Math.min(2, effMatch - 3));
    const agencyContrib = salary * (agencyMatchPct / 100);
    const totalAnnualContrib = annualContrib + agencyContrib;

    // Year-by-year projection (compounded monthly, contrib annually for simplicity)
    const series = [];
    let bal = currentBalance;
    for (let y = 0; y <= years; y++) {
      series.push({
        age: currentAge + y,
        balance: bal,
        balanceToday: bal / Math.pow(1 + infl, y), // today's dollars
      });
      bal = bal * (1 + r) + totalAnnualContrib;
    }
    const finalBalance = series.length ? series[series.length - 1].balance : currentBalance;
    const finalBalanceToday = series.length
      ? series[series.length - 1].balanceToday
      : currentBalance;

    // 4% safe-withdrawal rule → monthly income in retirement
    const monthlyIncomeToday = (finalBalanceToday * 0.04) / 12;
    const monthlyIncomeNominal = (finalBalance * 0.04) / 12;

    // Rule-of-thumb target: 10x salary by 65
    const goalTarget = salary * 10;
    const onTrack = finalBalanceToday >= goalTarget;
    const coveragePct = goalTarget > 0
      ? Math.min(100, (finalBalanceToday / goalTarget) * 100)
      : 0;

    return {
      years,
      annualContrib,
      agencyContrib,
      agencyMatchPct,
      totalAnnualContrib,
      series,
      finalBalance,
      finalBalanceToday,
      monthlyIncomeToday,
      monthlyIncomeNominal,
      goalTarget,
      onTrack,
      coveragePct,
      capturingFullMatch: contribPct >= 5,
    };
  }, [currentAge, retireAge, currentBalance, salary, contribPct, expectedReturn, inflation]);

  // Brief visual pulse when any input changes — makes "live" obvious
  useEffect(() => {
    setRecalcPulse(true);
    const t = setTimeout(() => setRecalcPulse(false), 400);
    return () => clearTimeout(t);
  }, [currentAge, retireAge, currentBalance, salary, contribPct, expectedReturn, inflation]);

  // Load saved scenarios (once)
  useEffect(() => {
    scenariosAPI
      .list()
      .then((r) => setScenarios(r.data || []))
      .catch(() => {});
  }, []);

  // Fetch lifecycle suggestion when ages change
  useEffect(() => {
    if (currentAge > 0 && retireAge > currentAge) {
      lifecycleAPI
        .suggest(currentAge, retireAge)
        .then((r) => setLifecycleSuggestion(r.data))
        .catch(() => setLifecycleSuggestion(null));
    }
  }, [currentAge, retireAge]);

  const saveScenario = async () => {
    const name = (scenarioName || '').trim();
    if (!name) return;
    if (scenarios.length >= 5) {
      toast.error('Maximum 5 scenarios. Delete one first.');
      return;
    }
    setSavingScenario(true);
    try {
      const palette = ['#1B2A4A', '#8B6B3B', '#3B8C6E', '#B8860B', '#2D5F8A'];
      const color = palette[scenarios.length % palette.length];
      const r = await scenariosAPI.create({
        name,
        color,
        lifecycle_fund: lifecycleSuggestion?.suggested_code || null,
        inputs: {
          current_age: currentAge,
          retirement_age: retireAge,
          current_balance: currentBalance,
          salary,
          contribution_pct: contribPct,
          expected_return_pct: expectedReturn,
          inflation_pct: inflation,
        },
      });
      setScenarios((s) => [...s, r.data]);
      setScenarioName('');
      toast.success(`Scenario "${name}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingScenario(false);
    }
  };

  const deleteScenario = async (id) => {
    try {
      await scenariosAPI.remove(id);
      setScenarios((s) => s.filter((sc) => sc.id !== id));
      setCompareIds((c) => c.filter((x) => x !== id));
      toast.success('Scenario deleted');
    } catch (_) {
      toast.error('Delete failed');
    }
  };

  const loadScenario = (sc) => {
    setCurrentAge(sc.inputs.current_age);
    setRetireAge(sc.inputs.retirement_age);
    setCurrentBalance(sc.inputs.current_balance);
    setSalary(sc.inputs.salary);
    setContribPct(sc.inputs.contribution_pct);
    setExpectedReturn(sc.inputs.expected_return_pct);
    setInflation(sc.inputs.inflation_pct);
    toast.success(`Loaded "${sc.name}"`);
  };

  const toggleCompare = (id) =>
    setCompareIds((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  // Pre-compute compare-scenario projections for overlay
  const compareProjections = useMemo(() => {
    return compareIds.map((id) => {
      const sc = scenarios.find((x) => x.id === id);
      if (!sc) return null;
      const inp = sc.inputs;
      const years = Math.max(0, inp.retirement_age - inp.current_age);
      const r = inp.expected_return_pct / 100;
      const empContrib = inp.salary * (inp.contribution_pct / 100);
      const effMatch = Math.min(inp.contribution_pct, 5);
      const agencyMatchPct =
        1 + Math.min(3, effMatch) + 0.5 * Math.max(0, Math.min(2, effMatch - 3));
      const totalAnnual = empContrib + inp.salary * (agencyMatchPct / 100);
      const series = [];
      let bal = inp.current_balance;
      for (let y = 0; y <= years; y++) {
        series.push(bal);
        bal = bal * (1 + r) + totalAnnual;
      }
      return { id: sc.id, name: sc.name, color: sc.color, series };
    }).filter(Boolean);
  }, [compareIds, scenarios]);

  // SVG sparkline dimensions
  const W = 520;
  const H = 140;
  const pad = { l: 8, r: 8, t: 10, b: 18 };
  const xs = projection.series.map((_, i) => i);
  const ys = projection.series.map((s) => s.balance);
  const ysToday = projection.series.map((s) => s.balanceToday);
  const maxY = Math.max(1, ...ys);
  const xScale = (i) =>
    pad.l + ((W - pad.l - pad.r) * i) / Math.max(1, projection.series.length - 1);
  const yScale = (v) => H - pad.b - ((H - pad.t - pad.b) * v) / maxY;
  const pathNominal = ys.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(v)}`).join(' ');
  const pathToday = ysToday.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(v)}`).join(' ');
  const areaNominal = `${pathNominal} L ${xScale(xs.length - 1)} ${H - pad.b} L ${xScale(0)} ${H - pad.b} Z`;

  // ─── Sliders ──────────────────────────────
  const Slider = ({ label, value, min, max, step, onChange, suffix, testId }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-medium text-[#1B2A4A]">{label}</label>
        <span
          data-testid={`${testId}-value`}
          className={`font-mono text-[12px] font-semibold text-[#1B2A4A] transition-colors ${
            recalcPulse ? 'text-emerald-600' : ''
          }`}
        >
          {suffix === '$' ? fmt0(value) : `${value}${suffix || ''}`}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step || 1}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full accent-[#1B2A4A]"
      />
      <div className="flex justify-between text-[9px] text-[#9CA3AF]">
        <span>{suffix === '$' ? fmt0(min) : `${min}${suffix || ''}`}</span>
        <span>{suffix === '$' ? fmt0(max) : `${max}${suffix || ''}`}</span>
      </div>
    </div>
  );

  return (
    <div className="panel p-5" data-testid="retirement-goal-calculator">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Target size={15} className="text-[#1B2A4A]" />
          <span className="overline">Retirement Goal Calculator</span>
          <span
            data-testid="retirement-goal-live-badge"
            className={`inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border transition-colors ${
              recalcPulse
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-gray-50 text-[#6B7280] border-[#E2E5E9]'
            }`}
          >
            <Sparkles size={9} className={recalcPulse ? 'animate-pulse' : ''} /> Live
          </span>
        </div>
        <span className="text-[10px] text-[#9CA3AF]">
          Assumptions: annual comp · 4% safe withdrawal · FERS match
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — sliders */}
        <div className="space-y-4">
          <Slider testId="rgc-current-age"  label="Current age"         value={currentAge}      min={22} max={70} onChange={setCurrentAge}       suffix="" />
          <Slider testId="rgc-retire-age"   label="Retirement age"      value={retireAge}       min={Math.max(55, currentAge + 1)} max={75} onChange={setRetireAge} suffix="" />
          <Slider testId="rgc-salary"       label="Annual salary"       value={salary}          min={30000} max={300000} step={1000} onChange={setSalary}      suffix="$" />
          <Slider testId="rgc-balance"      label="Current balance"     value={currentBalance}  min={0}     max={3000000} step={1000} onChange={setCurrentBalance} suffix="$" />
          <Slider testId="rgc-contrib"      label="Your contribution"   value={contribPct}      min={0}     max={20}      step={0.5}  onChange={setContribPct}   suffix="%" />
          <Slider testId="rgc-return"       label="Expected return"     value={expectedReturn}  min={2}     max={12}      step={0.25} onChange={setExpectedReturn} suffix="%" />
          <Slider testId="rgc-inflation"    label="Inflation"           value={inflation}       min={0}     max={6}       step={0.25} onChange={setInflation}    suffix="%" />
        </div>

        {/* RIGHT — results */}
        <div className="space-y-4">
          <div
            className={`rounded-lg p-4 border transition-colors ${
              projection.onTrack
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-amber-50 border-amber-200'
            }`}
            data-testid="rgc-headline"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
                  Projected balance at {retireAge}
                </p>
                <p
                  data-testid="rgc-final-balance"
                  className="font-mono text-3xl font-semibold text-[#1B2A4A] leading-tight"
                >
                  {fmt0(projection.finalBalance)}
                </p>
                <p className="text-[11px] text-[#6B7280]">
                  <span className="font-mono">{fmt0(projection.finalBalanceToday)}</span>{' '}
                  in today's dollars
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
                  Monthly income
                </p>
                <p
                  data-testid="rgc-monthly-income"
                  className="font-mono text-2xl font-semibold text-[#1B2A4A] leading-tight"
                >
                  {fmt0(projection.monthlyIncomeToday)}
                </p>
                <p className="text-[11px] text-[#6B7280]">(4% rule, today's $)</p>
              </div>
            </div>

            {/* Coverage bar vs 10×-salary rule-of-thumb */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                <span>
                  Goal: 10× salary ={' '}
                  <span className="font-mono text-[#1B2A4A]">
                    {fmt0(projection.goalTarget)}
                  </span>
                </span>
                <span data-testid="rgc-on-track-badge" className={`font-semibold ${
                  projection.onTrack ? 'text-emerald-700' : 'text-amber-700'
                }`}>
                  {projection.onTrack ? (
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle2 size={10} /> On track
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <AlertTriangle size={10} /> Below target
                    </span>
                  )}
                </span>
              </div>
              <div className="w-full h-1.5 bg-white/70 rounded overflow-hidden border border-[#E2E5E9]">
                <div
                  className={projection.onTrack ? 'bg-emerald-500' : 'bg-amber-500'}
                  style={{
                    width: `${projection.coveragePct}%`,
                    height: '100%',
                    transition: 'width 0.3s ease-out',
                  }}
                />
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                {projection.coveragePct.toFixed(0)}% of rule-of-thumb goal
              </p>
            </div>
          </div>

          {/* Growth chart */}
          <div className="bg-white border border-[#E2E5E9] rounded-lg p-3" data-testid="rgc-chart-wrapper">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
                Projected growth
              </span>
              <div className="flex items-center gap-2 text-[9px]">
                <span className="inline-flex items-center gap-1 text-[#1B2A4A]">
                  <span className="w-2 h-0.5 bg-[#1B2A4A] inline-block" /> Nominal
                </span>
                <span className="inline-flex items-center gap-1 text-[#8B6B3B]">
                  <span className="w-2 h-0.5 bg-[#8B6B3B] inline-block" /> Today's $
                </span>
              </div>
            </div>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full h-auto"
              data-testid="rgc-chart"
            >
              <path d={areaNominal} fill="#1B2A4A" opacity="0.08" />
              <path d={pathNominal} fill="none" stroke="#1B2A4A" strokeWidth="2" />
              <path d={pathToday} fill="none" stroke="#8B6B3B" strokeWidth="1.25" strokeDasharray="3 3" />
              {/* Compare-scenario overlays */}
              {compareProjections.map((cp) => {
                const cpMax = Math.max(maxY, ...cp.series);
                const cpXScale = (i) =>
                  pad.l + ((W - pad.l - pad.r) * i) / Math.max(1, cp.series.length - 1);
                const cpYScale = (v) =>
                  H - pad.b - ((H - pad.t - pad.b) * v) / cpMax;
                const cpPath = cp.series
                  .map((v, i) => `${i === 0 ? 'M' : 'L'} ${cpXScale(i)} ${cpYScale(v)}`)
                  .join(' ');
                return (
                  <path
                    key={cp.id}
                    d={cpPath}
                    fill="none"
                    stroke={cp.color}
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                    opacity="0.85"
                    data-testid={`rgc-chart-compare-${cp.id}`}
                  />
                );
              })}
              <line x1={pad.l} y1={H - pad.b} x2={W - pad.r} y2={H - pad.b} stroke="#E2E5E9" />
            </svg>
            <div className="flex justify-between text-[9px] text-[#9CA3AF] px-1">
              <span>Age {currentAge}</span>
              <span>Age {retireAge}</span>
            </div>
          </div>

          {/* Annual contribution breakdown */}
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="rgc-contrib-breakdown">
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-2">
              <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">
                Your annual
              </p>
              <p className="font-mono text-sm font-semibold text-[#1B2A4A]" data-testid="rgc-your-annual">
                {fmt0(projection.annualContrib)}
              </p>
            </div>
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-2">
              <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">
                Agency ({projection.agencyMatchPct.toFixed(1)}%)
              </p>
              <p className="font-mono text-sm font-semibold text-emerald-700" data-testid="rgc-agency-annual">
                {fmt0(projection.agencyContrib)}
              </p>
            </div>
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-2">
              <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">
                Total annual
              </p>
              <p className="font-mono text-sm font-semibold text-[#1B2A4A]" data-testid="rgc-total-annual">
                {fmt0(projection.totalAnnualContrib)}
              </p>
            </div>
          </div>

          {!projection.capturingFullMatch && (
            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-[11px] text-amber-800 flex items-start gap-2" data-testid="rgc-match-warning">
              <AlertTriangle size={12} className="mt-0.5 text-amber-600" />
              <span>
                Raise your contribution to <strong>5%</strong> to capture the full
                FERS agency match — that's an extra{' '}
                <strong>{fmt0(salary * 0.05 - projection.annualContrib + salary * (0.05 - projection.agencyMatchPct / 100))}</strong>{' '}
                a year.
              </span>
            </div>
          )}

          {/* Lifecycle Fund suggestion */}
          {lifecycleSuggestion && (
            <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3" data-testid="rgc-lifecycle-suggestion">
              <div className="flex items-start gap-2">
                <Layers size={13} className="mt-0.5 text-[#1B2A4A]" />
                <div className="flex-1">
                  <p className="text-[11px] font-semibold text-[#1B2A4A]">
                    Consider:{' '}
                    <span className="text-[#8B6B3B]">
                      {lifecycleSuggestion.fund.display_name}
                    </span>
                  </p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">
                    {lifecycleSuggestion.rationale} Target allocation:{' '}
                    {Object.entries(lifecycleSuggestion.fund.allocation)
                      .filter(([, v]) => v > 0)
                      .map(([k, v]) => `${k.toUpperCase()} ${v}%`)
                      .join(' · ')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Save & Compare Scenarios */}
          <div className="border border-[#E2E5E9] rounded p-3" data-testid="rgc-scenarios">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">
                Saved scenarios ({scenarios.length}/5)
              </p>
            </div>
            <div className="flex gap-2 mb-2">
              <input
                data-testid="rgc-scenario-name"
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveScenario()}
                placeholder="Name this scenario (e.g. Baseline)"
                maxLength={60}
                disabled={scenarios.length >= 5}
                className="flex-1 h-8 px-2 text-[11px] border border-[#D1D5DB] rounded focus:outline-none focus:border-[#1B2A4A] disabled:bg-gray-50"
              />
              <button
                data-testid="rgc-save-scenario-btn"
                onClick={saveScenario}
                disabled={savingScenario || !scenarioName.trim() || scenarios.length >= 5}
                className="inline-flex items-center gap-1 h-8 px-3 rounded text-[11px] font-semibold text-white bg-[#1B2A4A] hover:bg-[#0F1A33] disabled:opacity-50"
              >
                <Save size={11} />
                Save
              </button>
            </div>
            {scenarios.length === 0 && (
              <p className="text-[10px] text-[#9CA3AF] italic" data-testid="rgc-scenarios-empty">
                Save up to 5 scenarios to compare side-by-side on the chart.
              </p>
            )}
            <div className="space-y-1">
              {scenarios.map((sc) => {
                const selected = compareIds.includes(sc.id);
                return (
                  <div
                    key={sc.id}
                    data-testid={`rgc-scenario-row-${sc.id}`}
                    className="flex items-center justify-between gap-2 text-[11px] p-1.5 rounded hover:bg-[#FAFBFC]"
                  >
                    <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleCompare(sc.id)}
                        className="accent-[#1B2A4A]"
                      />
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: sc.color }}
                      />
                      <span className="font-semibold text-[#1B2A4A] truncate">{sc.name}</span>
                      <span className="text-[10px] text-[#6B7280] font-mono">
                        age {sc.inputs.current_age}→{sc.inputs.retirement_age} · {sc.inputs.contribution_pct}%
                      </span>
                    </label>
                    <button
                      data-testid={`rgc-load-scenario-${sc.id}`}
                      onClick={() => loadScenario(sc)}
                      className="px-2 h-6 text-[10px] font-semibold text-[#1B2A4A] border border-[#D1D5DB] rounded hover:bg-gray-50"
                    >
                      Load
                    </button>
                    <button
                      data-testid={`rgc-delete-scenario-${sc.id}`}
                      onClick={() => deleteScenario(sc.id)}
                      title="Delete scenario"
                      className="w-6 h-6 inline-flex items-center justify-center text-[#B91C1C] hover:bg-red-50 rounded"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
